import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {TopbarService} from 'src/app/services/front/topbar.service';
import {TranslateService} from '@ngx-translate/core';
import {FormControl, FormGroup} from '@angular/forms';
import {
	distinctUntilChanged,
	finalize,
	forkJoin,
	from,
	interval,
	merge,
	Observable,
	of,
	Subscription,
	switchMap,
	tap
} from 'rxjs';
import {
	ApplicationElasticTableData,
	ApplicationExport,
	ApplicationSortColumn,
	ApplicationStatus,
	ApplicationTableData,
	ApplicationTableFilterForm,
	Health,
	HealthStatus,
	HostingType
} from 'src/app/services/model/new-application.model';
import {ApplicationAutodiscover} from 'src/app/services/model/autodiscover.model';
import {
	ApplicationDetailComponent,
	ApplicationDetailInput
} from 'src/app/modules/home/applications/application-detail/application-detail.component';
import {
	AutodiscoverComponent,
	AutoDiscoverComponentData
} from 'src/app/modules/home/applications/autodiscover/autodiscover.component';
import {RightSliderService} from 'src/app/services/front/right-slider.service';
import {CurrentTenantService} from 'src/app/services/front/current-tenant.service';
import {ApplicationAutodiscoverService} from 'src/app/services/back/application-autodiscover.service';
import {NewApplicationService} from 'src/app/services/back/new-application.service';
import {debounceTime, filter, first, map, take} from 'rxjs/operators';
import {CatalogComponent, CatalogDialogResult} from 'src/app/modules/home/applications/catalog-v2/catalog.component';
import {MatDialog} from '@angular/material/dialog';
import {CriticalityLevel} from 'src/app/services/tenant.service';
import {Category} from 'src/app/services/model/application-category.model';
import {Organization, OrganizationService, OrganizationTree} from 'src/app/services/organization.service';
import {TenantAccountService} from 'src/app/services/back/tenant-account.service';
import {ApplicationCategoryService} from 'src/app/services/back/application-category.service';
import {TenantAccount} from 'src/app/services/model/account.model';
import {ActivatedRoute, Router} from '@angular/router';
import {QueryRangeType} from 'src/app/services/back/tenant-finance.service';
import {Usage} from 'src/app/services/model/application-contract.model';
import {CatalogTag} from 'src/app/services/model/catalog-tag.model';
import {ExportService, ExportType} from 'src/app/services/front/export.service';
import {CostPipe} from "../../../../pipes/number/cost.pipe";

@Component({
	templateUrl: './application-list.component.html',
	standalone: false,
	styleUrls: ['./application-list.component.scss']
})
export class ApplicationListComponent implements OnInit, OnDestroy {

	tenantId: string;

	_initializing: boolean;
	_loading: boolean;
	_loadingAutoDiscover: boolean;
	_exporting: boolean;

	isEditor: boolean;

	elasticData: ApplicationElasticTableData[] = [];
	applicationRows: ApplicationRow[] = [];
	autodiscoverList: ApplicationAutodiscover[] = [];
	globalAppCount?: number;

	sortForm = new FormControl({
		column: ApplicationSortColumn.NAME, direction: SortDirection.ASC
	}, {nonNullable: true});

	column: typeof ApplicationSortColumn = ApplicationSortColumn;
	direction: typeof SortDirection = SortDirection;

	filters: ApplicationTableFilterForm = {};

	readonly FIRST_SIZE: number = 20;
	readonly MAX_SIZE: number = 1000;

	refreshSub: Subscription = new Subscription();
	loadingSub: Subscription = new Subscription();
	subscription: Subscription = new Subscription();

	constructor(private applicationDiscoveredService: ApplicationAutodiscoverService,
				private currentTenantService: CurrentTenantService,
				private applicationService: NewApplicationService,
				private rightSliderService: RightSliderService,
				private activatedRoute: ActivatedRoute,
				private exportService: ExportService,
				private topbarService: TopbarService,
				private translate: TranslateService,
				private dialog: MatDialog,
				private cost: CostPipe,
				private router: Router) {
	}

	ngOnInit(): void {
		this.setAppCount();
		this.subscription.add(this.currentTenantService.getInitializingChanges()
			.subscribe(initializing => this._initializing = initializing));
		this.subscription.add(this.currentTenantService.getCurrentTenantChanges()
			.pipe(tap(tenant => {
				this.tenantId = tenant.configuration.id;
				this.isEditor = tenant.configuration.role !== 'read_only';
			}))
			.subscribe(() => this.initialize()));
		this.subscription.add(this.currentTenantService.getApplicationCountChanges()
			.subscribe(count => this.setAppCount(count)));
		this.subscription.add(this.activatedRoute.queryParams
			.pipe(filter(params => params?.applicationId), first())
			.subscribe(params => this.openApplicationDrawer(params.applicationId)));
	}

	private setAppCount(count?: number): void {
		this.globalAppCount = count;
		this.topbarService.onTitleChange(
			this.translate.instant('menu.applications'),
			this.translate.instant('menu.subtitle.applications'),
			count !== undefined && count > 0 ? count.toString() : '-'
		);
	}

	initialize(): void {
		this.applicationRows = [];
		this.fetchElasticTableData();
		this.fetchProgressiveApplicationTableData();
		this.fetchAutodiscoverList();
	}

	private fetchElasticTableData(): void {
		this.subscription.add(this.applicationService.getAllApplicationElasticTableData(this.tenantId)
			.subscribe(elasticData => this.setElasticData(elasticData)));
	}

	private fetchProgressiveApplicationTableData(): void {
		this.loadingSub = this.setLoading(true)
			.pipe(
				switchMap(() => {
					this.filters.limit = this.FIRST_SIZE;
					return this.applicationService.getAllApplicationTableData(this.tenantId, this.filters)
				}),
				tap(applications => this.setApplicationRows(applications)),
				switchMap(() => {
					this.filters.limit = this.MAX_SIZE;
					this.filters.offset = this.FIRST_SIZE;
					return this.applicationService.getAllApplicationTableData(this.tenantId, this.filters)
				}),
				tap(applications => this.pushApplicationRows(applications)),
				finalize(() => {
					this.filters.limit = undefined;
					this.filters.offset = undefined;
					this.setLoading(false)
				}))
			.subscribe();
	}

	fetchAllApplicationByFilter(loading = true): Observable<{}> {
		this.loadingSub.unsubscribe();
		return this.setLoading(loading).pipe(
			switchMap(() => this.applicationService.getAllApplicationTableData(this.tenantId, this.filters)),
			tap(applications => this.setApplicationRows(applications)),
			switchMap(() => this.setLoading(false)));
	}

	onFilterChanged(filters: ApplicationTableFilterForm) {
		this.filters = filters;
		this.fetchAllApplicationByFilter().subscribe();
	}

	private setElasticData(elasticData: ApplicationElasticTableData[]): void {
		this.elasticData = elasticData;
		if (this.applicationRows.length > 0) {
			this.applicationRows = this.applicationRows
				.map(app => this.buildApplicationRows(app));
			this.applicationRows.sort(this.sortData);
		}
	}

	private setApplicationRows(applications: ApplicationTableData[]): void {
		this.applicationRows = applications
			.map(app => this.buildApplicationRows(app));
		this.applicationRows.sort(this.sortData);
	}

	private pushApplicationRows(applications: ApplicationTableData[]): void {
		this.applicationRows.push(...applications
			.map(app => this.buildApplicationRows(app)));
		this.applicationRows.sort(this.sortData);
	}

	private buildApplicationRows(app: ApplicationTableData): ApplicationRow {
		const elasticData: ApplicationElasticTableData | undefined = this.elasticData.find(d => d.applicationId === app.instance.applicationId);
		return {
			instance: app.instance,
			category: app.category,
			cost: app.cost,
			usage: !app.instance.usageActivated ? undefined : elasticData?.usage,
			health: !app.instance.usageActivated ? undefined : (elasticData?.health ?? {
				percent: 100,
				status: HealthStatus.GOOD,
				queryType: QueryRangeType.PAST_1_MONTH
			}),
		};
	}

	private fetchAutodiscoverList(): void {
		this.subscription.add(this.switchLoadingAutodiscover()
			.pipe(
				switchMap(() => this.applicationDiscoveredService.getAllApplicationAutodiscover(this.tenantId)),
				tap(autodiscoverList => this.autodiscoverList = autodiscoverList),
				finalize(() => this.switchLoadingAutodiscover()))
			.subscribe());
	}

	sortBy(column: ApplicationSortColumn): void {
		const sort: ApplicationSort = {
			column: column,
			direction: this.sortFormValue.column === column && this.sortFormValue.direction === SortDirection.ASC
				? SortDirection.DESC
				: SortDirection.ASC
		}
		if (!this._loading || column === ApplicationSortColumn.USAGE || column === ApplicationSortColumn.HEALTH) {
			this.sortForm.setValue(sort, {emitEvent: false});
			this.applicationRows = this.applicationRows.sort(this.sortData);
		} else {
			this.sortForm.setValue(sort);
		}
	}

	private sortData = (data1: ApplicationRow, data2: ApplicationRow): number => {
		const direction: number = this.sortFormValue.direction === SortDirection.ASC ? 1 : -1;
		switch (this.sortFormValue.column) {
			case ApplicationSortColumn.NAME:
				return data1.instance.name.localeCompare(data2.instance.name) * direction;
			case ApplicationSortColumn.CATEGORY:
				return ((data1.category?.name ?? 'zzzzz').localeCompare(data2.category?.name ?? 'zzzzz') * direction);
			case ApplicationSortColumn.CRITICALITY:
				const order: (CriticalityLevel | null | undefined)[] = [CriticalityLevel.HIGH, CriticalityLevel.MEDIUM, CriticalityLevel.LOW, null, undefined];
				return (order.indexOf(data2.instance.criticality) - order.indexOf(data1.instance.criticality)) * direction;
			case ApplicationSortColumn.USAGE:
				return data1.usage?.value !== data2.usage?.value
					? ((data1.usage?.value ?? -1) > (data2.usage?.value ?? -1) ? 1 : -1) * direction
					: (data1.instance.name.localeCompare(data2.instance.name));
			case ApplicationSortColumn.HEALTH:
				return ((data1.health?.percent ?? -1) > (data2.health?.percent ?? -1) ? 1 : -1) * direction;
			case ApplicationSortColumn.COST:
				return ((data1.cost.value ?? -1) > (data2.cost.value ?? -1) ? 1 : -1) * direction;
			default:
				return 0;
		}
	}

	get sortFormValue(): ApplicationSort {
		return this.sortForm.value;
	}

	openAutoDiscoverDrawer(): void {
		const data: AutoDiscoverComponentData = {
			discoveredApplications: this.autodiscoverList
		}
		this.rightSliderService.openComponent(AutodiscoverComponent, data)
			.pipe(switchMap(() => this.fetchAllApplicationByFilter()))
			.subscribe();
	}

	openCreateApplication(): void {
		this.dialog.open<CatalogComponent, any, CatalogDialogResult>(CatalogComponent).afterClosed()
			.pipe(
				filter(result => !!result?.success),
				switchMap(() => this.fetchAllApplicationByFilter()))
			.subscribe();
	}

	resetQueryParamsObservable(): Observable<boolean> {
		return from(
			this.router.navigate([], {
				queryParams: {
					'selectedApplicationTab': null,
					'settings': null,
					'comments': null,
					'serverId': null
				},
				queryParamsHandling: 'merge'
			})
		);
	}

	exportCsv = () => this.export(ExportType.CSV);
	exportXlsx = () => this.export(ExportType.XLSX);

	private export(type: ExportType): void {
		this.switchExporting()
			.pipe(
				map(() => this.applicationRows.map(a => a.instance.applicationId)),
				switchMap(ids => this.applicationService.getAllApplicationExportDataByIds(this.tenantId, ids)),
				map(data => this.buildApplicationExportData(data)),
				tap(data => this.exportService.export(type, data, 'applications')),
				finalize(() => this.switchExporting()))
			.subscribe();
	}

	private buildApplicationExportData(data: ApplicationExport[]): ApplicationExportCsv[] {
		return data.map(app => ({
			name: app.instance.name,
			description: app.instance.description ?? '',
			criticality: app.instance.criticality ? this.translate.instant('page.appDetails.businessCriticality.' + app.instance.criticality.toLowerCase()) : '',
			category: app.category?.name ?? '',
			health: app.health.status ? this.translate.instant('page.dashboard.typology.' + app.health.status) : '',
			usage: app.usage.value ? app.usage.value.toString() : '',
			cost: app.cost.value ? app.cost.value.toString() + ' ' + localStorage.getItem('currency') : '',
			supportEmail: app.instance.supportEmail ?? '',
			supportPhone: app.instance.supportPhone ?? '',
			url: app.instance.accessUrl ?? '',
			servers: app.servers.map(s => s.name).join(', '),
			technologies: app.technologies.map(t => t.technology.name).join(', '),
			tags: app.tags.map(t => t.name).join(', '),
			responsibles: app.responsibles.map(r => r.account.firstName + ' ' + r.account.lastName).join(', '),
			lifecycle: app.lifecycle ? 'Start date : ' + (app.lifecycle.phaseInDate || '-') + ' End date : ' + (app.lifecycle.phaseOutDate || '-') + ' Decommissioning data : ' + (app.lifecycle.retiredDate || '-') : '',
			contracts: app.contracts.map(c => 'Amount : ' + this.cost.transform(c.amount || 0) + ' End date : ' + (c.contract.endDate || '-')).join(', ')
		}));
	}

	openApplicationDrawer(applicationId: string): void {
		const data: ApplicationDetailInput = {applicationId: applicationId};
		this.resetQueryParamsObservable()
			.pipe(
				switchMap(() => this.rightSliderService.openComponent(ApplicationDetailComponent, data)),
				switchMap(() => this.fetchAllApplicationByFilter(false)),
				switchMap(() => this.resetQueryParamsObservable())
			).subscribe();
	}

	get pendingAutodiscoverAppCount(): number {
		return this.autodiscoverList.filter(a => a.status === 'active').length;
	}

	private setLoading(loading: boolean): Observable<{}> {
		this._loading = loading;
		return of({});
	}

	private switchLoadingAutodiscover(): Observable<{}> {
		this._loadingAutoDiscover = !this._loadingAutoDiscover;
		return of({});
	}

	private switchExporting(): Observable<{}> {
		this._exporting = !this._exporting;
		return of({});
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
		this.loadingSub.unsubscribe();
		this.refreshSub.unsubscribe();
	}
}

enum SortDirection {
	ASC = 'asc',
	DESC = 'desc'
}

interface ApplicationSort {
	column: ApplicationSortColumn;
	direction: SortDirection;
}

interface ApplicationRow extends ApplicationTableData {
	usage?: Usage;
	health?: Health;
}

interface ApplicationExportCsv {
	name: string;
	description: string;
	criticality: string;
	category: string;
	health: string;
	usage: string;
	cost: string;
	supportEmail: string;
	supportPhone: string;
	url: string;
	servers: string;
	technologies: string;
	tags: string;
	responsibles: string;
	lifecycle: string;
	contracts: string;
}
